import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Upload, Loader2, Lock } from 'lucide-react';
import api from '@/services/api';

/**
 * Public, magic-link patient page for uploading requested documents.
 * Mobile-first — most patients will hit this from a phone after
 * tapping the link in the email.
 */
export default function DocumentUploadPage() {
  const { token } = useParams();
  // A new magic link must never inherit another recipient's data or signature.
  return <DocumentUploadSession key={token} token={token} />;
}

function DocumentUploadSession({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingItem, setUploadingItem] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const uploadLock = useRef(false);
  const recoveryKey='cervica_upload_operation_v1_'+token;
  const pendingUpload = useRef(undefined);
  if(pendingUpload.current===undefined){try{pendingUpload.current=JSON.parse(localStorage.getItem(recoveryKey)||'null');if(pendingUpload.current&&(!pendingUpload.current.operationId||!pendingUpload.current.itemId))pendingUpload.current={invalid:true};}catch{pendingUpload.current={invalid:true};}}
  const [recoveringUpload,setRecoveringUpload] = useState(!!pendingUpload.current);
  function clearPendingUpload(){localStorage.removeItem(recoveryKey);pendingUpload.current=null;setRecoveringUpload(false);}
  async function checkUpload(){if(uploadLock.current)return;uploadLock.current=true;setUploadingItem(pendingUpload.current?.itemId||'pending');try{const operation=pendingUpload.current;const {data:receipt}=await api.get(`/document-upload/${token}/operations/${operation.operationId}`);if(receipt.success!==true||receipt.operation_id!==operation.operationId||receipt.item_id!==operation.itemId||!receipt.document_id)throw Error('Receipt remains pending');clearPendingUpload();setUploadError(null);await reload();}catch{setUploadError('Upload is still unconfirmed. Retry the original file or contact the practice; a new upload remains blocked.');}finally{uploadLock.current=false;setUploadingItem(null);}}
  const loadVersion = useRef(0);

  async function reload() {
    const version = ++loadVersion.current;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/document-upload/${token}`);
      if (!response.data?.request || !Array.isArray(response.data.items)) throw new Error('Invalid request response');
      if (version === loadVersion.current) setData(response.data);
    } catch (e) {
      if (version === loadVersion.current) setError(e?.response?.data?.error || 'Could not load this request.');
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }

  useEffect(() => { reload(); return () => { loadVersion.current += 1; }; }, [token]);

  async function handleFile(itemId, file) {
    if (!file || uploadLock.current) return;
    if(pendingUpload.current && (pendingUpload.current.invalid || (pendingUpload.current.file && pendingUpload.current.file !== file) || pendingUpload.current.itemId !== itemId)) return;
    const retrying=!!pendingUpload.current;
    const hadOriginalFile=!!pendingUpload.current?.file;
    if (file.size > 25 * 1024 * 1024) {
      setUploadError('This file is larger than 25 MB. Please choose a smaller file.');
      return;
    }
    uploadLock.current = true;
    setUploadingItem(itemId);
    setUploadError(null);
    try {
      pendingUpload.current ||= {file,itemId,operationId:crypto.randomUUID()};
      const operation=pendingUpload.current;
      operation.file=file;
      localStorage.setItem(recoveryKey,JSON.stringify({operationId:operation.operationId,itemId:operation.itemId}));
      const fd = new FormData();
      fd.append('file', file);
      fd.append('item_id', itemId);
      fd.append('operation_id', operation.operationId);
      const response=await api.post(`/document-upload/${token}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout:60000,
      });
      if(response.data?.success!==true || response.data.operation_id!==operation.operationId || response.data.item_id!==itemId || !response.data.document_id) throw Error('Upload receipt not confirmed');
      clearPendingUpload();
      await reload();
    } catch (e) {
      if(retrying&&!hadOriginalFile&&e.response?.status===409)pendingUpload.current.file=null;
      if(!retrying&&[400,404,422].includes(e.response?.status)){try{clearPendingUpload()}catch{}}
      setRecoveringUpload(!!pendingUpload.current);
      setUploadError(e?.response?.data?.error || 'Upload could not be confirmed. Refresh the request to check whether it arrived before uploading again.');
    } finally {
      uploadLock.current = false;
      setUploadingItem(null);
    }
  }

  if (loading && !data) {
    return <FullPageStatus><p className="text-gray-500">Loading…</p></FullPageStatus>;
  }
  if (error) {
    return (
      <FullPageStatus>
        <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
        <p className="text-gray-900 font-semibold">{error}</p>
        <p className="text-gray-500 text-sm mt-2">If you believe this is a mistake, please call our office.</p>
        <button className="mt-4 underline" onClick={reload}>Refresh request</button>
      </FullPageStatus>
    );
  }

  const { request, patient, practice_name, items } = data;
  const requiredItems = items.filter((i) => i.required);
  const allDone = requiredItems.length > 0 && requiredItems.every((i) => i.uploaded_at);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#CC0000] text-white">
        <div className="max-w-2xl mx-auto px-5 py-6">
          <p className="text-[12px] uppercase tracking-wider opacity-90">{practice_name}</p>
          <h1 className="mt-1 text-2xl font-bold">Document Upload</h1>
          {patient?.first_name && (
            <p className="mt-1 text-[14px] opacity-90">For {patient.first_name}</p>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-4">
        {request.message_to_patient && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-[14px] text-amber-900">
            {request.message_to_patient}
          </div>
        )}

        {allDone && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900">All set — thank you!</p>
              <p className="text-[13px] text-emerald-800 mt-1">Your team has received everything they need. You can close this page.</p>
            </div>
          </div>
        )}

        {(uploadError || recoveringUpload) && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">
            {uploadError || (pendingUpload.current?.invalid ? 'The saved upload reference could not be read. Contact the practice before uploading again.' : 'An earlier upload needs confirmation.')}
            {recoveringUpload && !pendingUpload.current?.invalid && <button disabled={uploadingItem!==null} className="ml-2 underline" onClick={checkUpload}>Check uploaded receipt</button>}
            {recoveringUpload && !pendingUpload.current?.file && !pendingUpload.current?.invalid && <label className="block mt-2">Choose the same file to retry<input aria-label="Original file for retry" type="file" disabled={uploadingItem!==null} onChange={e=>handleFile(pendingUpload.current.itemId,e.target.files?.[0])}/></label>}
            {recoveringUpload && pendingUpload.current?.file && <button disabled={uploadingItem!==null} className="ml-2 underline" onClick={()=>handleFile(pendingUpload.current.itemId,pendingUpload.current.file)}>Retry same file</button>}
            <button className="ml-2 underline" disabled={uploadingItem !== null || recoveringUpload} onClick={reload}>Refresh request</button>
          </div>
        )}

        <div className="space-y-3">
          {items.map((item) => (
            <UploadItem
              key={item.id}
              item={item}
              uploading={uploadingItem === item.id}
              disabled={uploadingItem !== null || recoveringUpload}
              onFile={(file) => handleFile(item.id, file)}
            />
          ))}
        </div>

        <div className="rounded-lg bg-white border border-gray-200 p-4 text-[12px] text-gray-600 flex items-start gap-3">
          <Lock className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            Your uploads are encrypted in transit and at rest. Only your care team can view them.
            JPEG, PNG, HEIC, and PDF files up to 25 MB are accepted.
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadItem({ item, uploading, disabled, onFile }) {
  const inputRef = useRef(null);
  const isDone = !!item.uploaded_at;

  return (
    <div className={`rounded-xl border p-4 transition-colors ${isDone ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 text-[15px]">{item.label}</h3>
            {item.required && !isDone && (
              <span className="rounded bg-red-50 text-red-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase">Required</span>
            )}
          </div>
          {isDone ? (
            <p className="mt-1 text-[12px] text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Uploaded — you can replace it if needed
            </p>
          ) : (
            <p className="mt-1 text-[12px] text-gray-500">Tap to choose a photo or PDF</p>
          )}
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className={`rounded-lg px-3 py-2 text-[13px] font-semibold whitespace-nowrap transition-colors ${
            isDone
              ? 'border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50'
              : 'bg-[#CC0000] text-white hover:bg-[#b30000]'
          } disabled:opacity-60`}
        >
          {uploading
            ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</span>
            : (isDone
              ? <span className="inline-flex items-center gap-1.5"><Upload className="h-4 w-4" /> Replace</span>
              : <span className="inline-flex items-center gap-1.5"><Upload className="h-4 w-4" /> Upload</span>)
          }
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = ''; // allow re-selecting same file
        }}
      />
    </div>
  );
}

function FullPageStatus({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center flex flex-col items-center">
        {children}
      </div>
    </div>
  );
}
